'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';

// ─── Role Definitions ─────────────────────────────────────────────────────────
export type AppRole = 'Admin' | 'Field Team' | 'Viewer';

export interface RBACPermissions {
  canEditWellConstruction: boolean;      // Upload/delete construction docs, edit well details
  canWriteContaminationTracking: boolean; // Add/edit risk assessments, update contamination data
  canBulkUpdateWellStatus: boolean;      // Use Bulk Update modal in Monitoring Well Status
  canAddWell: boolean;                   // Add new wells
  canEditWellStatus: boolean;            // Edit individual well status/condition
}

const ROLE_PERMISSIONS: Record<AppRole, RBACPermissions> = {
  Admin: {
    canEditWellConstruction: true,
    canWriteContaminationTracking: true,
    canBulkUpdateWellStatus: true,
    canAddWell: true,
    canEditWellStatus: true,
  },
  'Field Team': {
    canEditWellConstruction: true,
    canWriteContaminationTracking: true,
    canBulkUpdateWellStatus: false,
    canAddWell: true,
    canEditWellStatus: true,
  },
  Viewer: {
    canEditWellConstruction: false,
    canWriteContaminationTracking: false,
    canBulkUpdateWellStatus: false,
    canAddWell: false,
    canEditWellStatus: false,
  },
};

// Map legacy demo-user roles to AppRole
function resolveAppRole(rawRole: string | undefined | null): AppRole {
  if (!rawRole) return 'Viewer';
  const r = rawRole.toLowerCase();
  if (r === 'admin') return 'Admin';
  if (r === 'engineer' || r === 'pjo' || r === 'field team') return 'Field Team';
  // Manager and Director get Admin-level access
  if (r === 'manager' || r === 'director') return 'Admin';
  // Owner gets Viewer access (read-only oversight)
  if (r === 'owner') return 'Viewer';
  return 'Viewer';
}

interface RBACContextValue {
  role: AppRole;
  permissions: RBACPermissions;
  can: (permission: keyof RBACPermissions) => boolean;
}

const RBACContext = createContext<RBACContextValue>({
  role: 'Viewer',
  permissions: ROLE_PERMISSIONS['Viewer'],
  can: () => false,
});

export const useRBAC = () => useContext(RBACContext);

export function RBACProvider({ children }: { children: React.ReactNode }) {
  const [role, setRole] = useState<AppRole>('Viewer');

  useEffect(() => {
    const readRole = () => {
      try {
        const stored = localStorage.getItem('envirowell_user');
        if (stored) {
          const user = JSON.parse(stored);
          setRole(resolveAppRole(user?.role));
        }
      } catch {
        setRole('Viewer');
      }
    };
    readRole();

    // Re-read on storage changes (e.g. login/logout in another tab)
    window.addEventListener('storage', readRole);
    return () => window.removeEventListener('storage', readRole);
  }, []);

  const permissions = ROLE_PERMISSIONS[role];

  const can = (permission: keyof RBACPermissions) => permissions[permission];

  return (
    <RBACContext.Provider value={{ role, permissions, can }}>
      {children}
    </RBACContext.Provider>
  );
}

// ─── Permission Gate Component ────────────────────────────────────────────────
interface PermissionGateProps {
  permission: keyof RBACPermissions;
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

export function PermissionGate({ permission, fallback = null, children }: PermissionGateProps) {
  const { can } = useRBAC();
  return can(permission) ? <>{children}</> : <>{fallback}</>;
}

// ─── Role Badge Component ─────────────────────────────────────────────────────
export function RoleBadge({ role }: { role: AppRole }) {
  const styles: Record<AppRole, string> = {
    Admin: 'bg-primary/10 text-primary border border-primary/20',
    'Field Team': 'bg-green-50 text-green-700 border border-green-200',
    Viewer: 'bg-secondary text-muted-foreground border border-border',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-700 ${styles[role]}`}>
      {role}
    </span>
  );
}
